'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import WorldwayNav from '@/components/WorldwayNav';

// ============================================================
// TYPES
// ============================================================

interface GADTour {
  gad_tour_id: string;
  name: string;
  description?: string;
  duration_days?: number;
  geography?: {
    region?: { name: string };
    countries?: Array<{ name: string }>;
  };
  trip_styles?: Array<{ name: string }>;
  categories?: Array<{ name: string }>;
  accommodation_level?: { name?: string };
  physical_grading?: { id?: number; label?: string };
  images?: Array<{ image_href?: string; type?: string }>;
  raw_data?: {
    structured_itineraries?: Array<{ start_city?: { name: string } }>;
    lowest_pp2a_prices?: Array<{ currency?: { iso_4217?: string }; amount?: string }>;
  };
}

interface FilterOption {
  id: string | number;
  name: string;
  count: number;
}

interface FilterData {
  regions: FilterOption[];
  travelStyles: FilterOption[];
  tourTypes: FilterOption[];
  serviceLevels: FilterOption[];
  physicalGradings: Array<{ id: number; name: string; count: number }>;
  collections: FilterOption[];
  promotions: FilterOption[];
  topCountries: Array<{ name: string; count: number }>;
  topStartCities: Array<{ name: string; count: number }>;
  durationRange: { min: number; max: number };
}

interface ActiveFilters {
  regions: string[];
  countries: string[];
  travelStyles: string[];
  tourTypes: string[];
  startCities: string[];
  serviceLevels: string[];
  physicalGradings: number[];
  collections: string[];
  promotions: string[];
  minDays: number;
  maxDays: number;
  departingAfter: string;
  departingBefore: string;
}

// ============================================================
// FILTER SECTION COMPONENT
// ============================================================

function FilterSection({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[var(--border)] py-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="text-xs font-semibold uppercase tracking-widest text-[var(--foreground)]">{title}</span>
        <span className="text-[var(--muted-foreground)] text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="mt-3 space-y-1.5">{children}</div>}
    </div>
  );
}

function CheckboxItem({
  label,
  count,
  checked,
  onChange,
}: {
  label: string;
  count?: number;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 cursor-pointer group">
      <div className="flex items-center gap-2">
        <div
          onClick={() => onChange(!checked)}
          className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all ${
            checked
              ? 'bg-[var(--primary)] border-[var(--primary)]'
              : 'border-[var(--border)] group-hover:border-[var(--primary)]/50'
          }`}
        >
          {checked && <span className="text-[8px] text-black font-bold">✓</span>}
        </div>
        <span
          onClick={() => onChange(!checked)}
          className={`text-xs transition-colors ${checked ? 'text-[var(--foreground)]' : 'text-[var(--muted-foreground)] group-hover:text-[var(--foreground)]'}`}
        >
          {label}
        </span>
      </div>
      {count !== undefined && (
        <span className="text-[10px] text-[var(--muted-foreground)]">{count.toLocaleString()}</span>
      )}
    </label>
  );
}

// ============================================================
// TOUR CARD
// ============================================================

function TourCard({ tour, onBook }: { tour: GADTour; onBook: (id: string, name: string) => void }) {
  const region = tour.geography?.region?.name ?? '';
  const countries = (tour.geography?.countries ?? []).map((c) => c.name).slice(0, 2).join(', ');
  const style = (tour.trip_styles ?? [])[0]?.name ?? '';
  const serviceLevel = tour.accommodation_level?.name ?? '';
  const grading = tour.physical_grading?.label ?? '';
  const gradingId = tour.physical_grading?.id ?? 0;
  const imageUrl = (tour.images ?? []).find((i) => i.type === 'BANNER')?.image_href
    ?? (tour.images ?? [])[0]?.image_href ?? '';
  const startCity = tour.raw_data?.structured_itineraries?.[0]?.start_city?.name ?? '';
  const priceData = tour.raw_data?.lowest_pp2a_prices?.[0];
  const price = priceData ? `From ${priceData.currency?.iso_4217 ?? 'USD'} ${Number(priceData.amount ?? 0).toLocaleString()}` : 'Price on request';

  const gradingColors: Record<number, string> = {
    1: 'text-emerald-400', 2: 'text-green-400', 3: 'text-amber-400', 4: 'text-orange-400', 5: 'text-red-400',
  };

  return (
    <div
      className="glass-card rounded-xl overflow-hidden hover:border-[rgba(201,168,76,0.3)] transition-all group cursor-pointer flex flex-col"
      onClick={() => onBook(tour.gad_tour_id, tour.name)}
    >
      {/* Image */}
      <div className="relative h-44 bg-[var(--surface-3)] overflow-hidden flex-shrink-0">
        {imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={imageUrl}
            alt={`${tour.name} tour - ${countries || region}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-5xl opacity-30">🌍</span>
          </div>
        )}
        {style && (
          <div className="absolute top-3 left-3">
            <span className="text-[10px] uppercase tracking-widest text-[var(--primary)] font-medium bg-black/70 backdrop-blur-sm px-2 py-0.5 rounded-full border border-[rgba(201,168,76,0.3)]">
              {style}
            </span>
          </div>
        )}
        {serviceLevel && (
          <div className="absolute top-3 right-3">
            <span className="text-[10px] uppercase tracking-widest text-white font-medium bg-black/70 backdrop-blur-sm px-2 py-0.5 rounded-full">
              {serviceLevel}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <h3
          className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors line-clamp-2 leading-snug"
          style={{ fontFamily: 'var(--font-serif)' }}
        >
          {tour.name}
        </h3>

        <div className="flex items-center gap-3 text-[11px] text-[var(--muted-foreground)] mb-2 flex-wrap">
          {region && <span>📍 {region}</span>}
          {tour.duration_days && <span>⏱ {tour.duration_days} days</span>}
          {startCity && <span>🛫 {startCity}</span>}
        </div>

        {countries && (
          <p className="text-[11px] text-[var(--muted-foreground)] mb-2">🌐 {countries}</p>
        )}

        {tour.description && (
          <p className="text-[11px] text-[var(--muted-foreground)] leading-relaxed line-clamp-2 mb-3 flex-1">
            {tour.description}
          </p>
        )}

        <div className="flex items-center justify-between mt-auto pt-2 border-t border-[var(--border)]">
          <div>
            <p className="text-sm font-semibold text-[var(--primary)]">{price}</p>
            {grading && (
              <p className={`text-[10px] ${gradingColors[gradingId] ?? 'text-[var(--muted-foreground)]'}`}>
                ⚡ {grading}
              </p>
            )}
          </div>
          <span className="text-xs text-[var(--muted-foreground)] group-hover:text-[var(--primary)] transition-colors">
            Book →
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN TOURS PAGE
// ============================================================

export default function ToursPage() {
  const router = useRouter();
  const [tours, setTours] = useState<GADTour[]>([]);
  const [totalTours, setTotalTours] = useState(0);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [source, setSource] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [showAllCities, setShowAllCities] = useState(false);
  const LIMIT = 24;

  const [filters, setFilters] = useState<ActiveFilters>({
    regions: [],
    countries: [],
    travelStyles: [],
    tourTypes: [],
    startCities: [],
    serviceLevels: [],
    physicalGradings: [],
    collections: [],
    promotions: [],
    minDays: 1,
    maxDays: 124,
    departingAfter: '',
    departingBefore: '',
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load filter options
  useEffect(() => {
    fetch('/api/gadventures/filters')
      .then((r) => r.json())
      .then((d) => {
        setFilterData(d.filters);
        setTotalTours(d.totalTours ?? 1194);
      })
      .catch(() => {});
  }, []);

  // Load tours
  const loadTours = useCallback(async (currentFilters: ActiveFilters, currentKeyword: string, currentPage: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(LIMIT));
      params.set('offset', String(currentPage * LIMIT));
      if (currentKeyword) params.set('q', currentKeyword);
      if (currentFilters.minDays > 1) params.set('min_days', String(currentFilters.minDays));
      if (currentFilters.maxDays < 124) params.set('max_days', String(currentFilters.maxDays));
      if (currentFilters.departingAfter) params.set('departing_after', currentFilters.departingAfter);
      if (currentFilters.departingBefore) params.set('departing_before', currentFilters.departingBefore);
      currentFilters.regions.forEach((r) => params.append('region', r));
      currentFilters.countries.forEach((c) => params.append('country', c));
      currentFilters.travelStyles.forEach((s) => params.append('travel_style', s));
      currentFilters.tourTypes.forEach((t) => params.append('tour_type', t));
      currentFilters.startCities.forEach((c) => params.append('start_city', c));
      currentFilters.serviceLevels.forEach((s) => params.append('service_level', s));
      currentFilters.physicalGradings.forEach((g) => params.append('physical_grading', String(g)));
      currentFilters.collections.forEach((c) => params.append('collection', c));

      const res = await fetch(`/api/gadventures/tours?${params}`);
      const data = await res.json();
      setTours(data.tours ?? []);
      setFilteredTotal(data.filteredTotal ?? data.total ?? 0);
      setSource(data.source ?? '');
    } catch {
      setTours([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadTours(filters, keyword, page);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filters, keyword, page, loadTours]);

  const toggleFilter = <K extends keyof ActiveFilters>(
    key: K,
    value: ActiveFilters[K] extends Array<infer V> ? V : never
  ) => {
    setPage(0);
    setFilters((prev) => {
      const arr = prev[key] as unknown[];
      const exists = arr.includes(value);
      return {
        ...prev,
        [key]: exists ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  };

  const clearAllFilters = () => {
    setPage(0);
    setFilters({
      regions: [], countries: [], travelStyles: [], tourTypes: [],
      startCities: [], serviceLevels: [], physicalGradings: [], collections: [],
      promotions: [], minDays: 1, maxDays: 124, departingAfter: '', departingBefore: '',
    });
    setKeyword('');
  };

  const activeFilterCount = [
    ...filters.regions, ...filters.countries, ...filters.travelStyles,
    ...filters.tourTypes, ...filters.startCities, ...filters.serviceLevels,
    ...filters.physicalGradings, ...filters.collections, ...filters.promotions,
  ].length + (filters.minDays > 1 || filters.maxDays < 124 ? 1 : 0)
    + (filters.departingAfter ? 1 : 0) + (filters.departingBefore ? 1 : 0);

  const handleBook = (id: string, name: string) => {
    router.push(`/booking?vertical=activity&supplier=g-adventures&product=${id}&destination=${encodeURIComponent(name)}`);
  };

  const filteredCountries = (filterData?.topCountries ?? []).filter((c) =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  );
  const filteredCities = (filterData?.topStartCities ?? []).filter((c) =>
    c.name.toLowerCase().includes(citySearch.toLowerCase())
  );

  const displayedCountries = showAllCountries ? filteredCountries : filteredCountries.slice(0, 15);
  const displayedCities = showAllCities ? filteredCities : filteredCities.slice(0, 15);

  // ============================================================
  // LEFT SIDEBAR FILTERS
  // ============================================================

  const SidebarContent = () => (
    <div className="text-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-[var(--border)]">
        <div>
          <h2 className="text-sm font-bold text-[var(--foreground)]">Filters</h2>
          <p className="text-[10px] text-[var(--muted-foreground)] mt-0.5">
            {totalTours.toLocaleString()} tours available
          </p>
        </div>
        {activeFilterCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="text-[10px] text-[var(--primary)] hover:underline"
          >
            Clear all ({activeFilterCount})
          </button>
        )}
      </div>

      {/* Tour Operator */}
      <FilterSection title="Tour Operator">
        <CheckboxItem
          label="G Adventures"
          count={totalTours}
          checked
          onChange={() => {}}
        />
      </FilterSection>

      {/* Departure Start Date */}
      <FilterSection title="Departure Start Date">
        <div className="space-y-2">
          <div>
            <label className="text-[10px] text-[var(--muted-foreground)] mb-1 block">Departing after</label>
            <input
              type="date"
              value={filters.departingAfter}
              onChange={(e) => { setPage(0); setFilters((p) => ({ ...p, departingAfter: e.target.value })); }}
              className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-1.5 text-xs text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]"
            />
          </div>
          <div>
            <label className="text-[10px] text-[var(--muted-foreground)] mb-1 block">Departing before</label>
            <input
              type="date"
              value={filters.departingBefore}
              onChange={(e) => { setPage(0); setFilters((p) => ({ ...p, departingBefore: e.target.value })); }}
              className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-1.5 text-xs text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]"
            />
          </div>
        </div>
      </FilterSection>

      {/* Promotions */}
      <FilterSection title="Promotions" defaultOpen={false}>
        {(filterData?.promotions ?? []).map((p) => (
          <CheckboxItem
            key={p.id}
            label={p.name}
            count={p.count}
            checked={filters.promotions.includes(String(p.id))}
            onChange={() => toggleFilter('promotions', String(p.id))}
          />
        ))}
      </FilterSection>

      {/* Region */}
      <FilterSection title="Region">
        {(filterData?.regions ?? []).map((r) => (
          <CheckboxItem
            key={r.id}
            label={r.name}
            count={r.count}
            checked={filters.regions.includes(r.name)}
            onChange={() => toggleFilter('regions', r.name)}
          />
        ))}
      </FilterSection>

      {/* Countries */}
      <FilterSection title="Countries">
        <input
          type="text"
          placeholder="Filter countries..."
          value={countrySearch}
          onChange={(e) => setCountrySearch(e.target.value)}
          className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-1.5 text-xs text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)] mb-2"
        />
        {displayedCountries.map((c) => (
          <CheckboxItem
            key={c.name}
            label={c.name}
            count={c.count}
            checked={filters.countries.includes(c.name)}
            onChange={() => toggleFilter('countries', c.name)}
          />
        ))}
        {filteredCountries.length > 15 && (
          <button
            onClick={() => setShowAllCountries(!showAllCountries)}
            className="text-[10px] text-[var(--primary)] hover:underline mt-1"
          >
            {showAllCountries ? 'Show less' : `Show ${filteredCountries.length - 15} more`}
          </button>
        )}
      </FilterSection>

      {/* Duration */}
      <FilterSection title="Duration (Days)">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={filters.maxDays}
              value={filters.minDays}
              onChange={(e) => { setPage(0); setFilters((p) => ({ ...p, minDays: Math.max(1, parseInt(e.target.value) || 1) })); }}
              className="w-20 bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-2 py-1.5 text-xs text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]"
            />
            <span className="text-xs text-[var(--muted-foreground)]">to</span>
            <input
              type="number"
              min={filters.minDays}
              max={124}
              value={filters.maxDays}
              onChange={(e) => { setPage(0); setFilters((p) => ({ ...p, maxDays: Math.min(124, parseInt(e.target.value) || 124) })); }}
              className="w-20 bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-2 py-1.5 text-xs text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]"
            />
            <span className="text-xs text-[var(--muted-foreground)]">days</span>
          </div>
          <input
            type="range"
            min={1}
            max={124}
            value={filters.maxDays}
            onChange={(e) => { setPage(0); setFilters((p) => ({ ...p, maxDays: parseInt(e.target.value) })); }}
            className="w-full accent-[var(--primary)]"
          />
        </div>
      </FilterSection>

      {/* Travel Style */}
      <FilterSection title="Travel Style">
        {(filterData?.travelStyles ?? []).map((s) => (
          <CheckboxItem
            key={s.id}
            label={s.name}
            count={s.count}
            checked={filters.travelStyles.includes(s.name)}
            onChange={() => toggleFilter('travelStyles', s.name)}
          />
        ))}
      </FilterSection>

      {/* Tour Type */}
      <FilterSection title="Tour Type">
        {(filterData?.tourTypes ?? []).map((t) => (
          <CheckboxItem
            key={t.id}
            label={t.name}
            count={t.count}
            checked={filters.tourTypes.includes(t.name)}
            onChange={() => toggleFilter('tourTypes', t.name)}
          />
        ))}
      </FilterSection>

      {/* Start City */}
      <FilterSection title="Start City" defaultOpen={false}>
        <input
          type="text"
          placeholder="Filter cities..."
          value={citySearch}
          onChange={(e) => setCitySearch(e.target.value)}
          className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-1.5 text-xs text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)] mb-2"
        />
        {displayedCities.map((c) => (
          <CheckboxItem
            key={c.name}
            label={c.name}
            count={c.count}
            checked={filters.startCities.includes(c.name)}
            onChange={() => toggleFilter('startCities', c.name)}
          />
        ))}
        {filteredCities.length > 15 && (
          <button
            onClick={() => setShowAllCities(!showAllCities)}
            className="text-[10px] text-[var(--primary)] hover:underline mt-1"
          >
            {showAllCities ? 'Show less' : `Show ${filteredCities.length - 15} more`}
          </button>
        )}
      </FilterSection>

      {/* Service Level */}
      <FilterSection title="Service Level">
        {(filterData?.serviceLevels ?? []).map((s) => (
          <CheckboxItem
            key={s.id}
            label={s.name}
            count={s.count}
            checked={filters.serviceLevels.includes(s.name)}
            onChange={() => toggleFilter('serviceLevels', s.name)}
          />
        ))}
      </FilterSection>

      {/* Physical Grading */}
      <FilterSection title="Physical Grading">
        {(filterData?.physicalGradings ?? []).map((g) => (
          <CheckboxItem
            key={g.id}
            label={g.name}
            count={g.count}
            checked={filters.physicalGradings.includes(g.id)}
            onChange={() => toggleFilter('physicalGradings', g.id)}
          />
        ))}
      </FilterSection>

      {/* Collections */}
      <FilterSection title="Collections" defaultOpen={false}>
        {(filterData?.collections ?? []).map((c) => (
          <CheckboxItem
            key={c.id}
            label={c.name}
            count={c.count}
            checked={filters.collections.includes(String(c.id))}
            onChange={() => toggleFilter('collections', String(c.id))}
          />
        ))}
      </FilterSection>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <WorldwayNav />

      {/* Hero */}
      <section className="relative pt-24 pb-14 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-xs text-[var(--muted-foreground)] mb-6">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-[var(--foreground)]">Tours</span>
          </nav>
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <p className="label-caps mb-3 text-[var(--primary)]">G Adventures · {totalTours.toLocaleString()}+ Tours</p>
              <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
                Guided Journeys,<br />
                <span className="gold-text-gradient">Extraordinary Moments.</span>
              </h1>
              <p className="text-base text-[var(--muted-foreground)] max-w-xl">
                Small group adventures across 100+ countries. Cultural immersions, active expeditions, wildlife safaris and more — powered by G Adventures.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link href="/tours/g-adventures" className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold">
                G Adventures Hub →
              </Link>
              <Link href="/ai-concierge" className="px-5 py-2.5 rounded-lg text-sm font-semibold border border-[var(--border)] hover:border-[var(--primary)]/50 transition-colors">
                🤖 AI Concierge
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <div className="border-t border-[var(--border)] bg-[var(--surface-2)] py-4 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] text-sm">🔍</span>
              <input
                type="text"
                placeholder="Search tours, destinations, themes..."
                value={keyword}
                onChange={(e) => { setKeyword(e.target.value); setPage(0); }}
                className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg pl-9 pr-4 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]"
              />
            </div>
            {/* Mobile filter toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[var(--border)] text-sm font-medium hover:border-[var(--primary)]/50 transition-colors"
            >
              <span>⚙️</span>
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-[var(--primary)] text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button onClick={clearAllFilters} className="hidden lg:block text-xs text-[var(--muted-foreground)] hover:text-[var(--primary)] transition-colors whitespace-nowrap">
                Clear filters ({activeFilterCount})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Left Sidebar — Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-36 max-h-[calc(100vh-10rem)] overflow-y-auto pr-2 scrollbar-thin">
              <SidebarContent />
            </div>
          </aside>

          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
              <div className="absolute left-0 top-0 bottom-0 w-80 bg-[var(--background)] border-r border-[var(--border)] overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bold text-[var(--foreground)]">Filters</h2>
                  <button onClick={() => setSidebarOpen(false)} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)]">✕</button>
                </div>
                <SidebarContent />
              </div>
            </div>
          )}

          {/* Tour Grid */}
          <div className="flex-1 min-w-0">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-sm text-[var(--foreground)] font-medium">
                  {loading ? 'Loading...' : `${filteredTotal > 0 ? filteredTotal.toLocaleString() : tours.length} tour${tours.length !== 1 ? 's' : ''} found`}
                </p>
                {source === 'mock' && (
                  <p className="text-[10px] text-amber-400 mt-0.5">
                    ⚠ Showing sample tours — run a full sync to import {totalTours.toLocaleString()}+ G Adventures tours
                  </p>
                )}
                {source === 'live' && (
                  <p className="text-[10px] text-emerald-400 mt-0.5">✓ Live data from G Adventures API</p>
                )}
                {source === 'db' && (
                  <p className="text-[10px] text-emerald-400 mt-0.5">✓ Synced from G Adventures catalogue</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--muted-foreground)]">Sort:</span>
                <select className="bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-1.5 text-xs text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]">
                  <option>Recommended</option>
                  <option>Price: Low to High</option>
                  <option>Price: High to Low</option>
                  <option>Duration: Short to Long</option>
                  <option>Duration: Long to Short</option>
                </select>
              </div>
            </div>

            {/* Active Filter Pills */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {filters.regions.map((r) => (
                  <span key={r} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.3)] text-[var(--primary)]">
                    {r}
                    <button onClick={() => toggleFilter('regions', r)} className="hover:text-white ml-0.5">✕</button>
                  </span>
                ))}
                {filters.countries.map((c) => (
                  <span key={c} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.3)] text-[var(--primary)]">
                    {c}
                    <button onClick={() => toggleFilter('countries', c)} className="hover:text-white ml-0.5">✕</button>
                  </span>
                ))}
                {filters.travelStyles.map((s) => (
                  <span key={s} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.3)] text-[var(--primary)]">
                    {s}
                    <button onClick={() => toggleFilter('travelStyles', s)} className="hover:text-white ml-0.5">✕</button>
                  </span>
                ))}
                {filters.serviceLevels.map((s) => (
                  <span key={s} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.3)] text-[var(--primary)]">
                    {s}
                    <button onClick={() => toggleFilter('serviceLevels', s)} className="hover:text-white ml-0.5">✕</button>
                  </span>
                ))}
                {filters.physicalGradings.map((g) => (
                  <span key={g} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.3)] text-[var(--primary)]">
                    Grading {g}
                    <button onClick={() => toggleFilter('physicalGradings', g)} className="hover:text-white ml-0.5">✕</button>
                  </span>
                ))}
                {filters.tourTypes.map((t) => (
                  <span key={t} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.3)] text-[var(--primary)]">
                    {t}
                    <button onClick={() => toggleFilter('tourTypes', t)} className="hover:text-white ml-0.5">✕</button>
                  </span>
                ))}
                {filters.collections.map((c) => (
                  <span key={c} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.3)] text-[var(--primary)]">
                    {c.replace(/-/g, ' ')}
                    <button onClick={() => toggleFilter('collections', c)} className="hover:text-white ml-0.5">✕</button>
                  </span>
                ))}
                {(filters.minDays > 1 || filters.maxDays < 124) && (
                  <span className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.3)] text-[var(--primary)]">
                    {filters.minDays}–{filters.maxDays} days
                    <button onClick={() => setFilters((p) => ({ ...p, minDays: 1, maxDays: 124 }))} className="hover:text-white ml-0.5">✕</button>
                  </span>
                )}
              </div>
            )}

            {/* Tour Cards */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="glass-card rounded-xl overflow-hidden animate-pulse">
                    <div className="h-44 bg-[var(--surface-3)]" />
                    <div className="p-4 space-y-2">
                      <div className="h-4 bg-[var(--surface-3)] rounded w-3/4" />
                      <div className="h-3 bg-[var(--surface-3)] rounded w-1/2" />
                      <div className="h-3 bg-[var(--surface-3)] rounded w-full" />
                      <div className="h-3 bg-[var(--surface-3)] rounded w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : tours.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-5xl mb-4">🌍</div>
                <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>No tours found</h3>
                <p className="text-sm text-[var(--muted-foreground)] mb-4">Try adjusting your filters or search terms.</p>
                <button onClick={clearAllFilters} className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold">
                  Clear All Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {tours.map((tour) => (
                  <TourCard key={tour.gad_tour_id} tour={tour} onBook={handleBook} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {!loading && tours.length > 0 && (
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-[var(--border)]">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-4 py-2 rounded-lg border border-[var(--border)] text-xs font-medium disabled:opacity-40 hover:border-[var(--primary)]/50 transition-colors"
                >
                  ← Previous
                </button>
                <span className="text-xs text-[var(--muted-foreground)]">
                  Page {page + 1} · Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, filteredTotal || tours.length)} of {(filteredTotal || totalTours).toLocaleString()}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={tours.length < LIMIT}
                  className="px-4 py-2 rounded-lg border border-[var(--border)] text-xs font-medium disabled:opacity-40 hover:border-[var(--primary)]/50 transition-colors"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sync CTA Banner */}
      {source === 'mock' && (
        <div className="border-t border-[var(--border)] bg-[var(--surface-2)] py-6">
          <div className="max-w-7xl mx-auto px-6">
            <div className="glass-card rounded-2xl p-6 border border-amber-500/20 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-3xl">⚡</span>
                <div>
                  <p className="text-sm font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
                    Import {totalTours.toLocaleString()}+ G Adventures Tours
                  </p>
                  <p className="text-xs text-[var(--muted-foreground)]">
                    Run a full catalogue sync to import all tours with complete details, itineraries, pricing, and availability.
                  </p>
                </div>
              </div>
              <Link
                href="/gadventures-integration-report"
                className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap"
              >
                Run Full Sync →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Browse Collections */}
      <section className="py-12 border-t border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'var(--font-serif)' }}>
            Explore More
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Tailor-Made Journeys', desc: 'Completely bespoke itineraries designed around your interests.', href: '/tailor-made', icon: '✨' },
              { title: 'Small Group Tours', desc: 'Intimate groups of up to 12 with expert local guides.', href: '/small-group', icon: '👥' },
              { title: 'AI Trip Builder', desc: 'Let our AI concierge design your perfect itinerary.', href: '/ai-concierge', icon: '🤖' },
            ].map((item) => (
              <Link
                key={item.title}
                href={item.href}
                className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group"
              >
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="text-sm font-bold mb-2 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>
                  {item.title}
                </h3>
                <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 WorldwayLuxe · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/activities" className="hover:text-[var(--primary)] transition-colors">Activities</Link>
            <Link href="/tailor-made" className="hover:text-[var(--primary)] transition-colors">Tailor-Made</Link>
            <Link href="/small-group" className="hover:text-[var(--primary)] transition-colors">Small Group</Link>
            <Link href="/tours/g-adventures" className="hover:text-[var(--primary)] transition-colors">G Adventures</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
